import React, { useState, useEffect, useRef } from 'react';
import { User, Group, Post, Comment } from '../types';

interface CommunityPageProps {
  user?: User | null;
  groups: Group[];
  setGroups: React.Dispatch<React.SetStateAction<Group[]>>;
  isEditorMode?: boolean;
  activeFilter?: 'All' | 'Update' | 'Question' | 'Insight' | 'Media';
  selectedGroupId?: string | null;
  setPosts?: React.Dispatch<React.SetStateAction<Post[]>>;
  posts?: Post[];
  searchTerm?: string;
  onSavePost?: (post: Post) => Promise<boolean>;
  onDeletePost?: (postId: string) => Promise<boolean>;
}

const CommunityPage: React.FC<CommunityPageProps> = ({ 
    user, 
    groups, 
    setGroups, 
    isEditorMode,
    activeFilter = 'All',
    selectedGroupId = null,
    setPosts,
    posts = [],
    searchTerm = '',
    onSavePost,
    onDeletePost
}) => {
  const [localPosts, setLocalPosts] = useState<Post[]>([]);
  
  // Use posts from prop if available
  const displayPosts = posts.length > 0 ? posts : localPosts;
  const [postInput, setPostInput] = useState('');
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [editInput, setEditInput] = useState('');
  const [showOptionsId, setShowOptionsId] = useState<string | null>(null);
  
  // Comment Section State
  const [expandedPostId, setExpandedPostId] = useState<string | null>(null);
  const [commentInput, setCommentInput] = useState('');

  // Parallax Refs
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bannerImageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
      const container = scrollContainerRef.current;
      const image = bannerImageRef.current;
      
      if (!container || !image) return;

      let rAFId: number;

      const handleScroll = () => {
          // Read immediate value
          const scrollTop = container.scrollTop;
          
          // Use requestAnimationFrame for smooth visual updates
          cancelAnimationFrame(rAFId);
          rAFId = requestAnimationFrame(() => {
              if (image) {
                  image.style.transform = `translate3d(0, ${scrollTop * 0.5}px, 0)`;
              }
          });
      };

      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
          container.removeEventListener('scroll', handleScroll);
          cancelAnimationFrame(rAFId);
      };
  }, []);

  // Sync with parent for sidebar counts
  useEffect(() => {
    if (setPosts) setPosts(localPosts);
  }, [localPosts, setPosts]);

  // Filtering Logic (Group, Category, Search)
  const filteredPosts = displayPosts.filter(post => {
      const matchesFilter = activeFilter === 'All' || post.category === activeFilter;
      const matchesGroup = !selectedGroupId || post.groupId === selectedGroupId;
      
      // Search Logic: Check Content, Author Name, or Comment Content
      let matchesSearch = true;
      if (searchTerm.trim()) {
          const lowerTerm = searchTerm.toLowerCase();
          matchesSearch = 
              post.content.toLowerCase().includes(lowerTerm) || 
              post.author.name.toLowerCase().includes(lowerTerm) ||
              post.comments.some(c => c.content.toLowerCase().includes(lowerTerm));
      }

      return matchesFilter && matchesGroup && matchesSearch;
  });

  const activeGroup = groups.find(g => g.id === selectedGroupId);

  const toggleLike = (postId: string) => {
      const post = displayPosts.find(p => p.id === postId);
      if (!post) return;

      const updatedPost = {
          ...post,
          likedByMe: !post.likedByMe,
          likes: post.likedByMe ? post.likes - 1 : post.likes + 1
      };

      if (onSavePost) {
          onSavePost(updatedPost);
      } else {
          setLocalPosts(prev => prev.map(p => p.id === postId ? updatedPost : p));
      }
  };

  const toggleComments = (postId: string) => {
      if (expandedPostId === postId) {
          setExpandedPostId(null);
      } else {
          setExpandedPostId(postId);
          setCommentInput(''); // Reset input when opening new thread
      }
  };

  const handleAddComment = (postId: string) => {
      if (!commentInput.trim() || !user) return;

      const post = displayPosts.find(p => p.id === postId);
      if (!post) return;

      const newComment: Comment = {
          id: `c-${Date.now()}`,
          author: {
              uid: user.uid,
              name: user.name,
              avatar: user.picture
          },
          content: commentInput.trim(),
          timestamp: Date.now()
      };

      const updatedPost = {
          ...post,
          comments: [...post.comments, newComment]
      };

      if (onSavePost) {
          onSavePost(updatedPost);
      } else {
          setLocalPosts(prev => prev.map(p => p.id === postId ? updatedPost : p));
      }

      setCommentInput('');

      // Trigger notification if commenter is not the author
      if (post.author.uid !== user.uid) {
        fetch('/api/notifications/notify-comment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            postAuthorUid: post.author.uid,
            commenterName: user.name,
            commentContent: newComment.content,
            postContent: post.content,
            postUrl: window.location.href
          })
        }).catch(err => console.error('Comment notification failed:', err));
      }
  };

  const handleCreatePost = () => {
    console.log('handleCreatePost called with input:', postInput);
    if (!postInput.trim() || !user) return;

    const newPost: Post = {
        id: `p-${Date.now()}`,
        author: {
            uid: user.uid,
            name: user.name,
            avatar: user.picture
        },
        content: postInput.trim(),
        timestamp: Date.now(),
        likes: 0,
        likedByMe: false,
        comments: [],
        category: activeFilter === 'All' ? 'Insight' : activeFilter,
        groupId: selectedGroupId || undefined,
        type: 'text'
    };

    console.log('New post created:', newPost);
    if (onSavePost) {
        onSavePost(newPost);
    } else {
        setLocalPosts(prev => [newPost, ...prev]);
    }
    setPostInput('');

    // Trigger broadcast notification via server
    fetch('/api/notifications/broadcast-new-post', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        authorName: newPost.author.name,
        content: newPost.content,
        groupId: newPost.groupId
      })
    }).then(res => res.json())
      .then(data => console.log('Broadcast response:', data))
      .catch(err => console.error('Broadcast failed:', err));
  };

  const handleDeletePost = (postId: string) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    if (onDeletePost) {
        onDeletePost(postId);
    } else {
        setLocalPosts(prev => prev.filter(p => p.id !== postId));
    }
    
    setShowOptionsId(null);
  };

  const startEditing = (post: Post) => {
    setEditingPostId(post.id);
    setEditInput(post.content);
    setShowOptionsId(null);
  };

  const handleUpdatePost = () => {
    if (!editingPostId || !editInput.trim()) return;
    
    const post = displayPosts.find(p => p.id === editingPostId);
    if (!post) return;

    const updatedPost = { ...post, content: editInput.trim() };
    
    if (onSavePost) {
        onSavePost(updatedPost);
    } else {
        setLocalPosts(prev => prev.map(p => p.id === editingPostId ? updatedPost : p));
    }
    
    setEditingPostId(null);
    setEditInput('');
  };

  if (isEditorMode) return <div className="p-20 text-center">Community Editor (Admin Only)</div>;

  return (
    <div className="w-full h-full flex flex-col overflow-hidden bg-[#fbfaf6] dark:bg-stone-900">
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto custom-scrollbar relative">
            
            {/* Banner Section with Parallax */}
            <div className="relative h-64 md:h-80 w-full overflow-hidden bg-stone-800">
                <img 
                    ref={bannerImageRef}
                    src={activeGroup ? activeGroup.image : "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?q=80&w=2000&auto=format&fit=crop"} 
                    className="w-full h-[120%] object-cover object-center absolute -top-[10%] left-0 opacity-80 will-change-transform"
                    alt="Community Banner" 
                    style={{ willChange: 'transform' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#fbfaf6] dark:from-stone-900 via-transparent to-transparent opacity-90"></div>
                
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 flex items-end">
                    <div className="max-w-3xl mx-auto w-full">
                        <h1 className="text-3xl md:text-5xl font-serif font-bold text-stone-800 dark:text-white drop-shadow-sm animate-in fade-in slide-in-from-top-8 duration-1000 ease-out">
                            {activeGroup ? activeGroup.name : "Community Feed"}
                        </h1>
                        <p className="text-stone-600 dark:text-stone-300 mt-2 text-sm md:text-base animate-in fade-in slide-in-from-bottom-2 duration-700 delay-300 fill-mode-backwards max-w-xl">
                            {activeGroup ? activeGroup.description : "Connect with fellow seekers, share insights from the readings, and grow together."}
                        </p>
                    </div>
                </div>
            </div>

            <div className="max-w-2xl mx-auto p-4 md:p-6 space-y-6 pb-20 relative z-10">
                {/* Compose Box Placeholder */}
                <div className="bg-white dark:bg-stone-800 p-4 rounded-xl border border-stone-200 dark:border-stone-700 shadow-sm flex gap-4 items-start animate-in fade-in zoom-in-95 duration-500 delay-100">
                    <div className="w-10 h-10 rounded-full bg-stone-100 dark:bg-stone-700 flex items-center justify-center shrink-0">
                        {user ? <img src={user.picture} className="w-full h-full rounded-full" alt="" /> : <i className="fa-solid fa-user text-stone-400"></i>}
                    </div>
                    <div className="flex-1">
                        <input 
                            id="community-post-input"
                            type="text" 
                            value={postInput}
                            onChange={(e) => setPostInput(e.target.value)}
                            onKeyDown={(e) => { if(e.key === 'Enter') handleCreatePost(); }}
                            placeholder="Share an insight or ask a question..." 
                            className="w-full bg-stone-50 dark:bg-stone-900 border-none rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-amber-500/20 outline-none transition-all" 
                        />
                        <div className="flex justify-between items-center mt-3">
                            <div className="flex gap-2">
                                <button id="community-post-image-btn" className="text-stone-400 hover:text-amber-500 transition-colors p-1"><i className="fa-solid fa-image"></i></button>
                                <button id="community-post-link-btn" className="text-stone-400 hover:text-amber-500 transition-colors p-1"><i className="fa-solid fa-link"></i></button>
                            </div>
                            <button 
                                id="community-post-submit-btn"
                                onClick={handleCreatePost}
                                disabled={!postInput.trim() || !user}
                                className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-1.5 rounded-lg text-xs font-bold transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                            >
                                Post
                            </button>
                        </div>
                    </div>
                </div>
                
                {filteredPosts.length === 0 ? (
                    <div className="text-center py-20 text-stone-400">
                        <i className="fa-regular fa-comments text-4xl mb-4"></i>
                        <p>{searchTerm ? "No posts match your search." : "No posts found in this feed."}</p>
                    </div>
                ) : (
                    filteredPosts.map((post, i) => (
                        <div key={post.id} className="bg-white dark:bg-stone-800/50 rounded-xl shadow-sm border border-stone-200 dark:border-stone-700 p-5 animate-in slide-in-from-bottom-4 fade-in duration-700 fill-mode-backwards" style={{ animationDelay: `${Math.min(i * 100 + 200, 1000)}ms` }}>
                            {/* Author Header */}
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <img src={post.author.avatar} alt="" className="w-10 h-10 rounded-full" />
                                    <div>
                                        <h4 className="font-bold text-stone-800 dark:text-stone-200 text-sm">{post.author.name}</h4>
                                        <p className="text-xs text-stone-400">{post.timestamp} • <span className="text-amber-600">{post.category}</span></p>
                                    </div>
                                </div>
                                {user && (user.uid === post.author.uid) && (
                                    <div className="relative">
                                        <button 
                                            onClick={() => setShowOptionsId(showOptionsId === post.id ? null : post.id)}
                                            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 dark:hover:bg-stone-700 text-stone-400 transition-colors"
                                        >
                                            <i className="fa-solid fa-ellipsis-vertical"></i>
                                        </button>
                                        
                                        {showOptionsId === post.id && (
                                            <div className="absolute right-0 mt-1 w-32 bg-white dark:bg-stone-800 rounded-lg shadow-lg border border-stone-100 dark:border-stone-700 py-1 z-20 animate-in fade-in zoom-in-95 duration-200">
                                                <button 
                                                    onClick={() => startEditing(post)}
                                                    className="w-full px-4 py-2 text-left text-xs text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700 flex items-center gap-2"
                                                >
                                                    <i className="fa-solid fa-pen text-[10px]"></i> Edit
                                                </button>
                                                <button 
                                                    onClick={() => handleDeletePost(post.id)}
                                                    className="w-full px-4 py-2 text-left text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center gap-2"
                                                >
                                                    <i className="fa-solid fa-trash text-[10px]"></i> Delete
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            
                            {/* Content */}
                            {editingPostId === post.id ? (
                                <div className="space-y-3 mb-4 animate-in fade-in duration-300">
                                    <textarea 
                                        value={editInput}
                                        onChange={(e) => setEditInput(e.target.value)}
                                        className="w-full bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-lg p-3 text-sm focus:ring-2 focus:ring-amber-500/20 outline-none min-h-[100px]"
                                    />
                                    <div className="flex justify-end gap-2">
                                        <button 
                                            onClick={() => setEditingPostId(null)}
                                            className="px-3 py-1.5 text-xs text-stone-500 hover:bg-stone-100 dark:hover:bg-stone-700 rounded-lg transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button 
                                            onClick={handleUpdatePost}
                                            disabled={!editInput.trim() || editInput === post.content}
                                            className="px-3 py-1.5 text-xs bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg transition-colors disabled:opacity-50"
                                        >
                                            Save Changes
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-stone-700 dark:text-stone-300 leading-relaxed text-sm mb-4 whitespace-pre-wrap">{post.content}</p>
                            )}
                            
                            {/* Actions */}
                            <div className="flex items-center gap-4 pt-4 border-t border-stone-100 dark:border-stone-700">
                                <button 
                                    onClick={() => toggleLike(post.id)}
                                    className={`flex items-center gap-2 text-sm ${post.likedByMe ? 'text-red-500' : 'text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 transition-colors'}`}
                                >
                                    <i className={`fa-${post.likedByMe ? 'solid' : 'regular'} fa-heart`}></i>
                                    <span>{post.likes}</span>
                                </button>
                                <button 
                                    onClick={() => toggleComments(post.id)}
                                    className={`flex items-center gap-2 text-sm transition-colors ${expandedPostId === post.id ? 'text-amber-600 font-bold' : 'text-stone-500 hover:text-stone-700 dark:hover:text-stone-300'}`}
                                >
                                    <i className="fa-regular fa-comment"></i>
                                    <span>{post.comments.length}</span>
                                </button>
                                <button className="ml-auto text-stone-400 hover:text-stone-600 transition-colors"><i className="fa-solid fa-share-nodes"></i></button>
                            </div>

                            {/* Collapsible Comment Section */}
                            {expandedPostId === post.id && (
                                <div className="mt-4 pt-4 border-t border-stone-100 dark:border-stone-700 bg-stone-50 dark:bg-stone-900/30 -mx-5 -mb-5 px-5 py-4 rounded-b-xl animate-in slide-in-from-top-2 duration-300">
                                    {/* Existing Comments */}
                                    <div className="space-y-4 mb-4">
                                        {post.comments.length > 0 ? (
                                            post.comments.map(comment => (
                                                <div key={comment.id} className="flex gap-3">
                                                    <img src={comment.author.avatar} className="w-8 h-8 rounded-full" alt="" />
                                                    <div className="flex-1 bg-white dark:bg-stone-800 p-3 rounded-xl rounded-tl-none border border-stone-100 dark:border-stone-700 text-sm shadow-sm">
                                                        <div className="flex justify-between items-center mb-1">
                                                            <span className="font-bold text-xs text-stone-700 dark:text-stone-300">{comment.author.name}</span>
                                                            <span className="text-[10px] text-stone-400">{comment.timestamp}</span>
                                                        </div>
                                                        <p className="text-stone-600 dark:text-stone-400">{comment.content}</p>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-center text-xs text-stone-400 italic py-2">No comments yet. Be the first!</p>
                                        )}
                                    </div>

                                    {/* Add Comment Input */}
                                    <div className="flex gap-3 items-center">
                                        <div className="w-8 h-8 rounded-full bg-stone-200 dark:bg-stone-700 flex items-center justify-center shrink-0 overflow-hidden">
                                            {user ? <img src={user.picture} className="w-full h-full" alt="" /> : <i className="fa-solid fa-user text-xs text-stone-400"></i>}
                                        </div>
                                        <div className="flex-1 relative">
                                            <input 
                                                type="text" 
                                                value={commentInput}
                                                onChange={(e) => setCommentInput(e.target.value)}
                                                onKeyDown={(e) => { if(e.key === 'Enter') handleAddComment(post.id); }}
                                                placeholder={user ? "Write a comment..." : "Sign in to comment..."} 
                                                disabled={!user}
                                                className="w-full bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-600 rounded-full px-4 py-2 text-xs focus:ring-2 focus:ring-amber-500/20 outline-none pr-10"
                                            />
                                            <button 
                                                onClick={() => handleAddComment(post.id)}
                                                disabled={!user || !commentInput.trim()}
                                                className="absolute right-1 top-1 w-7 h-7 bg-amber-500 text-white rounded-full flex items-center justify-center hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <i className="fa-solid fa-paper-plane text-[10px]"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    </div>
  );
};

export default CommunityPage;